import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

type UseWindowedGridOptions<T> = {
  items: Ref<T[]>;
  containerRef: Ref<HTMLElement | null>;
  itemSecondarySize: Ref<number>;
  rowHeight: Ref<number>;
  overscanRows?: number;
};

type WindowedGridResult<T> = {
  visibleItems: Ref<T[]>;
  topSpacerHeight: Ref<number>;
  bottomSpacerHeight: Ref<number>;
  itemsPerRow: Ref<number>;
};

export function useWindowedGrid<T>(options: UseWindowedGridOptions<T>): WindowedGridResult<T> {
  const { items, containerRef, itemSecondarySize, rowHeight, overscanRows = 2 } = options;
  const scrollY = ref(0);
  const viewportHeight = ref(0);
  const containerWidth = ref(0);
  const itemsPerRow = computed(() => {
    const width = containerWidth.value;
    const itemWidth = itemSecondarySize.value || 1;
    if (!width || !itemWidth) return 1;
    return Math.max(1, Math.floor(width / itemWidth));
  });

  const rowCount = computed(() => {
    const perRow = itemsPerRow.value || 1;
    return Math.ceil(items.value.length / perRow);
  });

  const totalHeight = computed(() => rowCount.value * rowHeight.value);

  function updateViewport() {
    scrollY.value = window.scrollY || window.pageYOffset || 0;
    viewportHeight.value = window.innerHeight || 0;
  }

  function updateContainerWidth() {
    const el = containerRef.value;
    containerWidth.value = el ? el.clientWidth : 0;
  }

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    updateViewport();
    updateContainerWidth();
    window.addEventListener('scroll', updateViewport, { passive: true });
    window.addEventListener('resize', updateViewport);
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateContainerWidth();
      });
      if (containerRef.value) {
        resizeObserver.observe(containerRef.value);
      }
    } else {
      window.addEventListener('resize', updateContainerWidth);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', updateViewport);
    window.removeEventListener('resize', updateViewport);
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    } else {
      window.removeEventListener('resize', updateContainerWidth);
    }
  });

  const visibleRange = computed(() => {
    const el = containerRef.value;
    const height = totalHeight.value;
    if (!el || height <= 0) {
      return { startRow: 0, endRow: -1 };
    }
    const containerTop = el.getBoundingClientRect().top + scrollY.value;
    const viewportTop = scrollY.value;
    const viewportBottom = scrollY.value + viewportHeight.value;
    const visibleTop = Math.max(0, viewportTop - containerTop);
    const visibleBottom = Math.min(height, viewportBottom - containerTop);
    if (visibleBottom <= 0 || visibleTop >= height) {
      return { startRow: 0, endRow: -1 };
    }
    const firstRow = Math.floor(visibleTop / rowHeight.value);
    const lastRow = Math.floor(Math.max(0, visibleBottom - 1) / rowHeight.value);
    const startRow = Math.max(0, firstRow - overscanRows);
    const endRow = Math.min(rowCount.value - 1, lastRow + overscanRows);
    return { startRow, endRow };
  });

  const visibleItems = computed(() => {
    const { startRow, endRow } = visibleRange.value;
    if (endRow < startRow) return [];
    const perRow = itemsPerRow.value || 1;
    const startIndex = startRow * perRow;
    const endIndex = Math.min(items.value.length - 1, (endRow + 1) * perRow - 1);
    return items.value.slice(startIndex, endIndex + 1);
  });

  const topSpacerHeight = computed(() => {
    const { startRow } = visibleRange.value;
    return Math.max(0, startRow * rowHeight.value);
  });

  const bottomSpacerHeight = computed(() => {
    const { endRow } = visibleRange.value;
    if (endRow < 0) return Math.max(0, totalHeight.value);
    return Math.max(0, (rowCount.value - endRow - 1) * rowHeight.value);
  });

  return {
    visibleItems,
    topSpacerHeight,
    bottomSpacerHeight,
    itemsPerRow,
  };
}
